import { AppNS } from 'app';
import { MAX_ATTENDEES, parseData as parseAttendees } from 'app/main/agency/bookings/report-details/attendees/components/attendee-form/validation-schema';
import { checkFailMatrix, parseQuestionnaire } from 'app/main/agency/bookings/report-details/inspection-questionnaire/inspection-questionnaire-helper';
import { parseQuestionsData } from 'app/main/agency/bookings/report-details/report-types/doee/compliance/inspection-checklist-form/inspection-checklist-helpers';
import { TREE_PROTECTION_FAIL_MATRIX } from 'app/main/agency/bookings/report-details/report-types/doee/compliance/inspection-checklist-form/inspection-checklist.const';
import { ONSITE_DOCUMENTS_FAIL_MATRIX, ONSITE_DOCUMENTS_LIST } from 'app/main/common/bookings/report-details/on-site-documents/on-site-docs-helpers';
import { FileInfo } from 'app/ui/components/multi-file-uploader/MultiFileUploader';

import { allowPaymentInvalidValue } from '../main/agency/bookings/config';
import {
    checkFailMatrix as checkTreeProtectionFailMatrix
} from '../main/agency/bookings/report-details/report-types/doee/compliance/inspection-checklist-form/inspection-checklist-helpers';
import { COMPLIANCE_TYPE, getFailMatrixByType, getQuestionsByType, PRE_CONSTRUCTION_TYPE } from '../main/common/bookings/report-details/report-types/doee/doee-helpers';
import { AgencyReportsState } from './agency-reports';

export function checkReportValid(state: AgencyReportsState): string[] {
  const report = state.currentReport;
  if (!report) {
    state.isReportValid = false;
    return [];
  }
  let validationErrors: Array<string> = [];

  if (!report.checkIn?.respondedAt) {
    validationErrors.push('Please Check In');
  }
  if (!report.checkOut?.respondedAt) {
    validationErrors.push('Please Check Out');
  }

  // all inspections items must have been set to other status than 'scheduled'
  if (
    report.eventInspections.length &&
    report.eventInspections.find((item) => item.status === 'scheduled') !== undefined
  ) {
    if (report.job.type === 'doee') {
      validationErrors.push('Please set INSPECTION RESULT');
    } else {
      validationErrors.push(`Please set status for all inspections (Cannot be 'Scheduled').`);
    }
  }

  if (!report.inspectionQuestionnaire) {
    if (report.job.type === 'doee') {
      validationErrors.push('Please fill in INSPECTION QUESTIONNAIRE section');
    } else {
      validationErrors.push('Please fill in "ON SITE DOCUMENTS" section');
    }
  }

  if (report.job.type === 'doee') {
    validationErrors = [...validationErrors, ...doeeValidation(report)];
  } else {
    if (checkIfOnsiteDocumentsFailed(report) && !checkIfAllInspectionResultsFailed(report)) {
      validationErrors.push(
        `All Inspections Result must be "Failed" when “No” is selected in any of On Site Documents questions`,
      );
    }
  }

  if (report.booking.paymentArrangement === 'event' && report.state === 'ready_for_issuance') {
    if (report.requestedAmount === null) {
      validationErrors.push('Please set total event cost.');
    } else if (!allowPaymentInvalidValue(report) && report.requestedAmount < 1) {
      validationErrors.push('Please set total event cost to amount greater than 0.');
    }
  }

  state.reportValidErrors = validationErrors;
  state.isReportValid = validationErrors.length ? false : true;

  return validationErrors;
}

function doeeValidation(report: AppNS.JobEventReport): string[] {
  let output = [];
  if (!checkInspectionChecklist(report)) {
    output.push('Please fill in INSPECTION QUESTIONNAIRE section');
  }

  if (!checkIfDoeeQuestionnaireFailedInconsistency(report)) {
    output.push('Inspection Result must be "Failed" when Inspection Questionnaire state is failed');
  }

  if (!checkIfDoeeChecklistFailedInconsistency(report)) {
    output.push('Inspection Result must be "Failed" when Inspection Checklist state is failed');
  }

  // SPECIAL VALIDATION FOR PRE-CONSTRUCTION REPORT
  if (report.eventInspections[0].inspectionType === PRE_CONSTRUCTION_TYPE) {
    output = [...output, ...checkPreConstructionReport(report)];
  }

  return output;
}

function checkInspectionChecklist(report: AppNS.JobEventReport): boolean {
  let result = true;
  if (
    report.eventInspections &&
    report.eventInspections[0].inspectionType === 'Compliance inspection' &&
    !report.inspectionChecklist
  ) {
    return false;
  }

  return result;
}

function checkIfDoeeQuestionnaireFailedInconsistency(report: AppNS.JobEventReport): boolean {
  if (report.eventInspections && report.eventInspections[0].status !== 'failed') {
    return !checkIfQuestionnaireFailed(report) === true;
  }

  return true;
}

function checkIfDoeeChecklistFailedInconsistency(report: AppNS.JobEventReport): boolean {
  if (report.eventInspections && report.eventInspections[0].status !== 'failed') {
    return !checkIfInspectionChecklistFailed(report);
  }

  return true;
}

function checkPreConstructionReport(report: AppNS.JobEventReport): string[] {
  const output: string[] = [];
  // Check Inspection Summary
  if (!report.notes) {
    output.push('Please fill in INSPECTION SUMMARY section');
  }

  // Check Attendees (there can be only one attendee with inspector role, there must be at least 2 attendees with signature, there can be no more than X attendees)
  const attendees = parseAttendees(report.documents?.filter((doc) => doc.type === 'report-attendee') || []);

  if (!attendees || attendees.length === 0) {
    output.push('Please add attendees');
  } else {
    const inspectorAttendees = attendees.filter((attendee) => attendee.role === 'inspector');
    if (inspectorAttendees.length > 1) {
      output.push('There can be only one attendee with inspector role');
    }

    const signedAttendees = attendees.filter((attendee) => attendee.documents[0].url);
    if (signedAttendees.length < 2) {
      output.push('There must be at least 2 attendees with signature');
    }

    if (attendees.length > MAX_ATTENDEES + 1) {
      output.push(`There can be no more than ${MAX_ATTENDEES} attendees`);
    }
  }

  // Check Documents
  const documents = report.documents?.filter((doc) => doc.type === 'report-document');
  // (there must be at least one document)
  if (!documents || documents.length === 0) {
    output.push('There must be at least one document');
  }

  // check inspection_questionnaire - must be some value
  if (!report.inspectionQuestionnaire || Object.keys(report.inspectionQuestionnaire).length === 0) {
    output.push('Please fill in INSPECTION QUESTIONNAIRE section');
  }

  return output;
}

export function checkIfInspectionChecklistFailed(report: AppNS.JobEventReport): boolean {
  if (
    report.eventInspections &&
    report.eventInspections[0].inspectionType === COMPLIANCE_TYPE &&
    report.inspectionChecklist &&
    report.inspectionChecklist.treeProtection
  ) {
    const treeProtection = parseQuestionsData(report.inspectionChecklist.treeProtection);

    if (checkTreeProtectionFailMatrix(treeProtection, TREE_PROTECTION_FAIL_MATRIX)) {
      return true;
    }
  }

  return false;
}

export function checkIfQuestionnaireFailed(report: AppNS.JobEventReport): boolean {
  if (report.inspectionQuestionnaire) {
    const questionnaire = getQuestionsByType(report);
    const failMatrix = getFailMatrixByType(report);

    if (checkFailMatrix(parseQuestionnaire(report, questionnaire), failMatrix)) {
      return true;
    }
  }

  return false;
}

// REGULAR REPORT
export function checkIfOnsiteDocumentsFailed(report: AppNS.JobEventReport): boolean {
  if (report.inspectionQuestionnaire && Object.keys(report.inspectionQuestionnaire).length > 0) {
    const questionnaire = ONSITE_DOCUMENTS_LIST;
    const failMatrix = ONSITE_DOCUMENTS_FAIL_MATRIX;

    if (checkFailMatrix(parseQuestionnaire(report, questionnaire), failMatrix)) {
      return true;
    }
  }

  return false;
}

function checkIfAllInspectionResultsFailed(report: AppNS.JobEventReport): boolean {
  if (report.eventInspections) {
    return report.eventInspections.every((event) => event.status === 'failed');
  }

  return false;
}

// function checkEventsInspectionsDocumentsValid(eventInspections: Array<AppNS.Inspection>): boolean {
//   let result = true;
//   eventInspections.forEach((inspection) => {
//     if (
//       inspection.status !== 'cancelled' &&
//       (!inspection.files || inspection.files.filter((d) => !d.isRemoved).length === 0)
//     ) {
//       result = false;
//     }
//   });
//   return result;
// }

export function setEventInspectionFileStatus(
  inspections: Array<AppNS.Inspection>,
  inspectionId: string,
  imageId: string,
  status: AppNS.Params,
): Array<AppNS.Inspection> {
  return inspections.map((inspection: AppNS.Inspection) => {
    if (inspection.id === inspectionId) {
      return {
        ...inspection,
        files: (inspection.files || []).map((image: FileInfo) => {
          if (image.id === imageId) {
            return { ...image, ...status };
          }
          return image;
        }),
      };
    } else {
      return inspection;
    }
  });
}
